import { Children, useEffect, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  LEFT,
  MEDIUM_VIEWPORT_MEDIA_QUERY,
  RIGHT,
  SCROLL_DISTANCE,
} from "../constants";
import { useChildAccessibility, useMediaQuery, useScrollState } from "../hooks";
import { ScreenReaderInstructions } from "./ScreenReaderInstructions";
import CarouselButton from "./CarouselButton";
import { ListElement } from "./ListElement";
import { withTranslation } from "../../translation";
import styles from "./HorizontalScrollList.css";
import { useScrollItemIntoCenter } from "../../hooks/useScrollItemIntoCenter";

const HorizontalScrollList = ({
  scrollSnapping,
  children,
  className,
  ariaLabel,
  formatTranslation,
}) => {
  const listRef = useRef(null);
  const childCount = Children.count(children);
  const isNotMobileViewport = useMediaQuery(MEDIUM_VIEWPORT_MEDIA_QUERY);
  const { hasOverflow, isAtStart, isAtEnd } = useScrollState(
    listRef,
    childCount,
  );
  const scrollItemIntoCenter = useScrollItemIntoCenter(listRef);
  const { handleChildAccessibility } = useChildAccessibility(
    listRef,
    isNotMobileViewport,
  );

  useEffect(() => {
    handleChildAccessibility();
  }, [handleChildAccessibility, hasOverflow]);

  useLayoutEffect(() => {
    const selected = listRef.current?.querySelector("span");
    const carouselUl = listRef.current;
    if (carouselUl) {
      if (selected) {
        scrollItemIntoCenter(selected);
      }
    }
  }, [scrollItemIntoCenter, listRef.current]);

  const scroll = (direction) => {
    if (!listRef.current) return;

    listRef.current.scrollBy({
      left: direction === LEFT ? -SCROLL_DISTANCE : SCROLL_DISTANCE,
      behavior: "smooth",
    });
  };

  const reserveCarouselControls = isNotMobileViewport;
  const showCarousel = hasOverflow && isNotMobileViewport;

  return (
    <div
      className={classnames(styles.listContainer, className)}
      role={showCarousel ? "region" : undefined}
      aria-label={showCarousel ? formatTranslation(ariaLabel) : undefined}
    >
      {showCarousel && (
        <ScreenReaderInstructions formatTranslation={formatTranslation} />
      )}
      {reserveCarouselControls && (
        <CarouselButton
          direction={LEFT}
          scroll={scroll}
          disabled={!showCarousel || isAtStart}
          isReserved={!showCarousel}
        />
      )}
      <ListElement
        listRef={listRef}
        scrollSnapping={scrollSnapping}
        showCarousel={showCarousel}
      >
        {children}
      </ListElement>
      {reserveCarouselControls && (
        <CarouselButton
          direction={RIGHT}
          scroll={scroll}
          disabled={!showCarousel || isAtEnd}
          isReserved={!showCarousel}
        />
      )}
    </div>
  );
};

HorizontalScrollList.propTypes = {
  scrollSnapping: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  formatTranslation: PropTypes.func.isRequired,
};

export default withTranslation(HorizontalScrollList);
